import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  Signal,
  signal,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  CompleteAppointmentService,
  DeleteAppointmentModalComponent,
  DeleteAppointmentModalComponentData,
  DeleteAppointmentModalComponentResult,
  SchedulesService,
} from '@libs/schedule';
import {
  appointmentHasParallelAppointments,
  createAppointmentActions,
  DEFAULT_START_HOUR,
  END_HOUR,
  INTERVAL_COUNT,
  ScheduleCalendarElement,
  ScheduleCalendarTime,
  ScheduleCalendarTimeWithOperatory,
  ScheduleOperatoryProviderAppointment,
  SHOWCASE_SCROLL_START_HOUR,
  START_HOUR,
  AppointmentActionType,
  selectAppointmentActionType,
  selectStatusOptions,
  selectTeeth,
  TIME_COLUMN_INTERVAL_COUNT,
  patientActions,
} from '@libs/store';
import { adjustedMinuteTimer$, TypedChanges, routes, HourToDayPartPipe, HourToDayPartHourPipe } from '@libs/utils';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs';
import { PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import { PatientDetails } from '@ffd/api-client';
import { Dialog } from '@angular/cdk/dialog';
import { HealthHistoryDialogComponent, HealthHistoryDialogData } from '@libs/home';
import { AddTreatmentItem } from '@libs/ui-lib';
import { PadStartPipe } from '../pad-start';
import { ScheduleTimeToGridRowPipe } from '../schedule-time-to-grid-row';
import { ScheduleCalendarTimeColumnComponent } from './schedule-calendar-time-column';
import { ScheduleCalendarMyDayComponent } from './schedule-calendar-my-day';
import { ScheduleCalendarItemComponent } from './schedule-calendar-item';
import { ScheduleCalendarHeaderComponent } from './schedule-calendar-header';
import { RowsSeparatorsAndTimeIndicatorComponent } from './rows-separators-and-time-indicator';
import { ScheduleCalendarVM } from './schedule-calendar.vm';

const TIME_COLUMN_WIDTH = 48;
const MY_DAY_COLUMN_WIDTH = 48;
const COLUMN_GAP = 4;
const ROW_HEIGHT = 10;
const ROW_HEIGHT_PX = '10px';
const DEFAULT_NUMBER_OF_OPERATORIES = 5;
const NUMBER_OF_COLUMN_LOADING_SKELETONS = DEFAULT_NUMBER_OF_OPERATORIES;
const NUMBER_OF_ROW_LOADING_SKELETONS = 16;
const SCROLL_INDICATOR_WIDTH = 20;
const MIN_WIDTH_OF_OPERATORIES = 68;
const COMPACT_VIEW_MIN_WIDTH_OF_OPERATORIES = 32;

@Component({
  selector: 'lib-schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrl: './schedule-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    HourToDayPartPipe,
    HourToDayPartHourPipe,
    ScheduleTimeToGridRowPipe,
    ScheduleCalendarItemComponent,
    ScheduleCalendarTimeColumnComponent,
    ScheduleCalendarHeaderComponent,
    ScheduleCalendarMyDayComponent,
    RowsSeparatorsAndTimeIndicatorComponent,
    PadStartPipe,
    TranslateModule,
    NgClass,
    PushTagOnClickDirective,
  ],
})
export class ScheduleCalendarComponent implements OnChanges, OnDestroy, AfterViewInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly schedulesService = inject(SchedulesService);
  private readonly completeAppointmentService = inject(CompleteAppointmentService);
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly dialog = inject(Dialog);

  @Input() public myDay: ScheduleCalendarElement[] = [];
  @Input() public operatories: ScheduleCalendarVM[] = [];
  @Input() public selectedAppointmentId?: number;
  @Input() public selectedEventId?: string;
  @Input() public isShowcase: boolean = false;
  @Input() public isToday: boolean = false;
  @Input() public isScheduleLoadingSignal: Signal<boolean> = signal(false);
  @Input() public isCompactViewSignal: Signal<boolean> = signal(false);
  @Input() public showTentativeSignal: Signal<boolean> = signal(false);
  @Input() public areTreatmentsLoadedSignal: boolean = false;
  public containerWidth = signal(0);
  protected readonly scrollIndicatorWidth = SCROLL_INDICATOR_WIDTH;

  @Output() public appointmentClick = new EventEmitter<number>();
  @Output() public appointmentDoubleClick = new EventEmitter();
  @Output() public newAppointment = new EventEmitter<ScheduleCalendarTimeWithOperatory>();
  @Output() public eventClick = new EventEmitter<string>();

  @ViewChild('rowsSeparatorsAndTimeIndicator') private readonly rowsSeparatorsAndTimeIndicator!: RowsSeparatorsAndTimeIndicatorComponent;
  @ViewChildren('myDayCalendarItem') private readonly myDayCalendarItems!: QueryList<ScheduleCalendarItemComponent>;

  protected readonly appointmentActionType = this.store.selectSignal(selectAppointmentActionType);
  protected readonly statusOptions = this.store.selectSignal(selectStatusOptions);
  protected readonly isEditAppointment = computed(() => this.appointmentActionType() === AppointmentActionType.Edit);

  protected readonly currentNumberOfOperatories = computed(() =>
    this.operatoriesSignal().length > 0 ? this.operatoriesSignal().length : DEFAULT_NUMBER_OF_OPERATORIES,
  );

  protected readonly calendarGridTemplateColumns = computed(() => {
    const allGaps = COLUMN_GAP * (this.currentNumberOfOperatories() - 1);
    const minWidth = this.isCompactViewSignal() ? COMPACT_VIEW_MIN_WIDTH_OF_OPERATORIES : MIN_WIDTH_OF_OPERATORIES;
    const operatoryColumnWidth = Math.max(
      minWidth,
      (this.containerWidth() - SCROLL_INDICATOR_WIDTH - allGaps) / this.currentNumberOfOperatories(),
    );
    return `repeat(${this.currentNumberOfOperatories()}, ${operatoryColumnWidth}px)`;
  });
  protected calendarGridTemplateRows = '';
  protected timeGridTemplateRows = '';

  protected readonly showcaseScrollStartHour = SHOWCASE_SCROLL_START_HOUR;
  protected readonly defaultStartHour = DEFAULT_START_HOUR;
  protected readonly intervalCount = INTERVAL_COUNT;
  protected readonly hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers, unicorn/consistent-function-scoping
  protected readonly minutesInTimeline = Array.from({ length: 60 / 10 }, (_, i) => i * 10);

  protected numberOfColumnLoadingSkeletons = NUMBER_OF_COLUMN_LOADING_SKELETONS;
  protected loadingGridTemplateColumns = `repeat(${DEFAULT_NUMBER_OF_OPERATORIES}, 1fr)`;
  protected columnLoadingSkeletons = Array.from({ length: this.numberOfColumnLoadingSkeletons }).fill(0);
  protected readonly rowLoadingSkeletons = Array.from({ length: NUMBER_OF_ROW_LOADING_SKELETONS }).fill(0);
  protected updateCount = signal(0);
  protected operatoriesSignal = signal<ScheduleCalendarVM[]>([]);
  protected isScrollAtEnd = signal(false);

  @ViewChild('overflowRef') public readonly scheduleOverflowRef!: ElementRef<HTMLDivElement>;
  @ViewChild('scheduleGridRef') public readonly scheduleGridRef!: ElementRef<HTMLDivElement>;
  @ViewChild('scrollGradientRef') public readonly scrollGradientRef!: ElementRef<HTMLDivElement>;

  protected readonly currentTimeWithTimeZone = signal(this.getCurrentTimeWithTimeZone());

  private getCurrentTimeWithTimeZone(): { hour: number; minute: number } {
    const time = DateTime.local().setZone('local');
    return {
      hour: time.hour + 1,
      minute: time.minute,
    };
  }
  protected readonly timePassedOfHourInPercent$ = adjustedMinuteTimer$.pipe(
    startWith(0),
    map(() => {
      this.updateCount.update((count) => count + 1);
      this.currentTimeWithTimeZone.set(this.getCurrentTimeWithTimeZone());
      return this.calculateTimePassedOfHourInPercent(this.currentTimeWithTimeZone());
    }),
  );
  protected readonly bodySubGridTemplate = `col-start-1 col-end-[-1] row-start-2 row-end-[-1] grid grid-cols-subgrid grid-rows-subgrid`;
  protected TagEvent = TagEvent;

  public containerHeight = signal((END_HOUR - START_HOUR) * INTERVAL_COUNT * ROW_HEIGHT);

  constructor(protected readonly renderer: Renderer2) {
    //header + rows
    this.calendarGridTemplateRows = `max-content repeat(${(END_HOUR - START_HOUR) * INTERVAL_COUNT} , ${ROW_HEIGHT_PX})`;
    this.timeGridTemplateRows = `repeat(${(END_HOUR - START_HOUR) * TIME_COLUMN_INTERVAL_COUNT} , 21px)`;

    this.activatedRoute.params
      .pipe(
        map((params) => params),
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.updateCount.set(0);
        this.schedulesService.removeAppointmentSelection();
        this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
      });

    effect(() => {
      // only after page reload and only after the first update, this ensures timeIndicator element is rendered
      if (this.updateCount() === 1) {
        this.scrollIntoView(this.isToday);
      }
    });

    effect(() => {
      // Trigger scroll position check when grid columns change
      this.calendarGridTemplateColumns();
      this.checkScrollPosition();
    });
  }

  public ngAfterViewInit(): void {
    this.myDayCalendarItems.changes.subscribe(() => {
      this.checkWidths();
    });
    this.onCalendarViewWidthChange();
    this.setupScrollListener();
  }

  public checkWidths(): void {
    this.myDayCalendarItems.forEach((item) => {
      if (item.elementRef.nativeElement) {
        const width = item.elementRef.nativeElement.offsetWidth;
        if (width === 0) {
          // remove outline highlight when selecting an appointment which is not shown in myDay (we only show 2 parallel appointments)
          this.renderer.addClass(item.elementRef.nativeElement, '!outline-0');
        }
      }
    });
  }

  public ngOnChanges(changes: TypedChanges<ScheduleCalendarComponent>): void {
    this.currentTimeWithTimeZone.set(this.getCurrentTimeWithTimeZone());
    if (changes['operatories']) {
      this.operatoriesSignal.set(this.operatories);
    }
  }

  public ngOnDestroy(): void {
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
  }

  public isSafari(): boolean {
    // exclude Chrome, Edge, and all Android browsers that include the Safari name in their user agent.
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  protected onCalendarItemClick(appointmentId: number): void {
    if (this.isEditAppointment()) return;
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    this.appointmentClick.emit(appointmentId);
  }

  protected onCalendarItemDoubleClick(): void {
    if (this.isEditAppointment()) return;
    this.appointmentDoubleClick.emit();
  }

  protected onEventClick(eventId: string): void {
    if (this.isEditAppointment()) return;
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    this.eventClick.emit(eventId);
  }

  public onTimeSlotClick({ hour, minute, operatory }: ScheduleCalendarTimeWithOperatory): void {
    if (this.isEditAppointment()) return;
    this.newAppointment.emit({ hour, minute, operatory });
  }

  public columnTemplateCount(index: number, myDay: ScheduleCalendarElement[] | undefined): number {
    const myDayColOffset = 0;
    return index + myDayColOffset + (myDay ? 1 : 0);
  }

  public calculateTimePassedOfHourInPercent(currentTime: ScheduleCalendarTime): number {
    return (currentTime.minute / 60) * 100;
  }

  public scrollToCurrentTime(): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollToCurrentTime();
  }

  public scrollToShowcaseStartHour(): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollToShowcaseStartHour();
  }

  public scrollToDefaultStartHour(): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollToDefaultStartHour();
  }

  /**
   * Scrolls the calendar to the current time or a specific hour based on the provided parameters.
   * @param isToday - A boolean indicating whether to scroll to the current time or not.
   */
  public scrollIntoView(isToday?: boolean): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollIntoView(isToday);
  }

  public appointmentHasParallelAppointments(appointment: ScheduleCalendarElement): boolean {
    return this.myDay ? appointmentHasParallelAppointments(appointment, this.myDay) : false;
  }

  protected onOpenHealthHistory(element: ScheduleCalendarElement): void {
    if (!element.patientId || !element.siteId) return;
    this.store.dispatch(
      patientActions.onOpenHealthHistory({
        patientId: element.patientId,
      }),
    );
    this.dialog.open<HealthHistoryDialogComponent, HealthHistoryDialogData>(HealthHistoryDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[1356px]', 'h-full', 'pt-[84px]', 'pb-[12px]'],
      data: {
        siteId: element.siteId,
        patientId: element.patientId,
      },
    });
  }

  public onScheduleEditClick(element: ScheduleCalendarElement): void {
    this.store.dispatch(createAppointmentActions.onSetIsEditAppointment({ isShowcase: element.siteId === routes.showcaseSiteId }));
    this.schedulesService.editSelectedAppointment({ isEdited: true });
  }

  public onDeleteClick(_element: ScheduleCalendarElement): void {
    this.dialog
      .open<DeleteAppointmentModalComponentResult, DeleteAppointmentModalComponentData>(DeleteAppointmentModalComponent, {
        backdropClass: ['bg-overlay-light'],
        panelClass: ['w-[480px]'],
        disableClose: true,
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.isDeleted) {
          this.schedulesService.deleteAppointment();
        }
      });
  }

  public onChangeStatus({ element, statusId }: { element: ScheduleCalendarElement; statusId: number }): void {
    const status = this.statusOptions().find((option) => option.id === statusId);
    const originalStatus = element.statusId ? this.statusOptions().find((option) => option.id === element.statusId) : undefined;

    if (!status) {
      return;
    }

    // Check if selected status is "Completed"
    const isCompletedStatus = status.description.toLowerCase().includes('completed');

    if (isCompletedStatus) {
      // Get appointment data to check for procedures
      const selectedAppointment = this.schedulesService.getAppointmentDataWithProcedures(element.id);

      if (selectedAppointment?.appointment.procedures && selectedAppointment.appointment.procedures.length > 0) {
        // Has procedures - reuse the existing complete appointment flow
        this.onCompleteAppointment(element);
        return;
      }
    }

    // No procedures or not completed status - just update status normally
    this.schedulesService.updateAppointmentStatus(element.id, status, element.isTentative ?? false, originalStatus);
  }

  public onCompleteAppointment(element: ScheduleCalendarElement): void {
    const selectedAppointment = this.schedulesService.getAppointmentDataWithProcedures(element.id);

    this.loadTeethAndOpenModal(element, selectedAppointment);
  }

  private loadTeethAndOpenModal(
    element: ScheduleCalendarElement,
    selectedAppointment: ScheduleOperatoryProviderAppointment | undefined,
  ): void {
    if (!selectedAppointment) {
      return;
    }

    const patientId = element.patientId!;
    const siteId = element.siteId!;

    this.store.dispatch(
      patientActions.onLoadTeeth({
        patientId,
        siteId,
        examCount: 5,
        silentReload: true,
      }),
    );

    this.store
      .select(selectTeeth)
      .pipe(
        filter((teeth) => teeth?.teethWithLayers !== undefined),
        take(1),
        switchMap((teethData) => {
          const patient: PatientDetails = {
            id: patientId,
            firstName: element.patientName.split(' ')[0] ?? '',
            lastName: element.patientName.split(' ').slice(1).join(' ') ?? '',
          } as PatientDetails;

          return this.completeAppointmentService.openCompleteAppointmentModal$({
            patient,
            appointmentDate: DateTime.fromISO(element.date),
            procedures: selectedAppointment.appointment.procedures,
            teeth: teethData?.teethWithLayers ?? [],
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((completedTreatments) => {
        this.handleCompletionResult(completedTreatments, element, selectedAppointment, patientId, siteId);
      });
  }

  private handleCompletionResult(
    completedTreatments: AddTreatmentItem[] | undefined,
    element: ScheduleCalendarElement,
    selectedAppointment: ScheduleOperatoryProviderAppointment,
    patientId: string,
    siteId: string,
  ): void {
    // Empty array or non-empty array means user clicked "Set to Complete"
    if (Array.isArray(completedTreatments) && completedTreatments.length > 0) {
      this.completeAppointmentService.completeTreatments({
        treatments: completedTreatments,
        patientId,
        siteId,
        provider: selectedAppointment.appointment.provider,
      });
    }

    // Update appointment status to completed
    const completedStatus = this.statusOptions().find((s) => s.description.toLowerCase().includes('completed'));
    if (completedStatus && completedTreatments !== undefined) {
      this.schedulesService.updateAppointmentStatus(element.id, completedStatus, element.isTentative ?? false);
    }
  }

  private readonly onCalendarViewWidthChange = (): void => {
    if (this.viewContainerRef) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width - TIME_COLUMN_WIDTH - MY_DAY_COLUMN_WIDTH;
          this.containerWidth.set(width);
          this.cdr.detectChanges();
        }
      });
      resizeObserver.observe(this.viewContainerRef?.element.nativeElement);
    }
  };

  private setupScrollListener(): void {
    if (this.scheduleOverflowRef) {
      this.scheduleOverflowRef.nativeElement.addEventListener('scroll', () => {
        this.checkScrollPosition();
      });
    }
  }

  private checkScrollPosition(): void {
    if (this.scheduleOverflowRef) {
      const element = this.scheduleOverflowRef.nativeElement;
      const offsetY = 10;
      const isAtEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - offsetY; // -offsetY for potential rounding errors
      this.isScrollAtEnd.set(isAtEnd);
    }
  }
}
